import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Search, RefreshCw, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import PromptModal from './PromptModal';

interface Prompt {
  id: string;
  name: string;
  prompt: string;
  notes?: string;
  usage: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PromptsView: React.FC = () => {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<Prompt | null>(null);

  const fetchPrompts = async () => {

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPrompts(data || []);
    } catch (error: any) {
      toast.error(`Error fetching prompts: ${error.message}`);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const filteredPrompts = prompts.filter(prompt => {
    const searchLower = searchQuery.toLowerCase();
    return (
      prompt.name.toLowerCase().includes(searchLower) ||
      prompt.prompt.toLowerCase().includes(searchLower) ||
      prompt.notes?.toLowerCase().includes(searchLower) ||
      prompt.usage.toLowerCase().includes(searchLower)
    );
  });

  const handleAddPrompt = () => {
    setCurrentPrompt(null);
    setIsModalOpen(true);
  };

  const handleEditPrompt = (prompt: Prompt) => {
    setCurrentPrompt(prompt);
    setIsModalOpen(true);
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const updateDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - updateDate.getTime()) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);

    let relativeTime = '';
    if (diffInMonths > 0) {
      relativeTime = `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    } else if (diffInDays > 0) {
      relativeTime = `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffInHours > 0) {
      relativeTime = `${diffInHours} ${diffInHours === 1 ? 'hr' : 'hrs'} ago`;
    } else {
      relativeTime = 'just now';
    }

    return `${updateDate.toLocaleDateString()} (${relativeTime})`;
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <MessageSquare className="w-8 h-8 mr-3 text-emerald-500" />
            AI Prompts
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your AI prompts and templates
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search prompts..."
              className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleAddPrompt}
            className="flex items-center px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Prompt
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        ) : filteredPrompts.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
              No prompts found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery ? 'Try adjusting your search' : 'Start by creating a prompt'}
            </p>
          </div>
        ) : (
          filteredPrompts.map((prompt) => (
            <div
              key={prompt.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer relative"
              onClick={() => handleEditPrompt(prompt)}
            >
              {prompt.is_active && (
                <div 
                  className="absolute top-4 right-4 w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20"
                  title="Active prompt"
                />
              )}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white pr-8">
                  {prompt.name}
                </h3>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {prompt.usage}
                </span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3 mb-4">
                {prompt.notes || 'No description provided'}
              </p>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                Last updated: {formatRelativeTime(prompt.updated_at)}
              </div>
            </div>
          ))
        )}
      </div>

      <PromptModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        prompt={currentPrompt}
        onSuccess={fetchPrompts}
      />
    </div>
  );
};

export default PromptsView;